
import Page from '@/Page.vue';
import { Component, Prop } from 'vue-property-decorator';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import {
    AuditEscalationGroupModel,
    AuditModel,
    AuditWorkplaceModel,
    EscalationGroupTypeEnum,
    GroupModel,
    LocationModel,
    PermissionTypeEnum,
    PlanItemModel,
    ProcessTypeEnum,
    TenantUserModel,
    WorkplaceModel,
} from '@/libs/Api';
import DashboardCardView from '@/libs/dashboard/components/DashboardCardView.vue';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { groupsStore } from '@/libs/groups/+state/store';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import IssueSolvingItemSmall from '@/libs/issues-solving/components/IssueSolvingItemSmall.vue';
import { locationsStore } from '@/libs/locations/+state/store';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import activeModeService from '@/libs/common/services/active-mode-service';
import IssueSolvingItem from '@/libs/issues-solving/components/IssueSolvingItem.vue';
import { auditEscalationGroupsStore } from '@/libs/audit-escalation-groups/+state/store';
import { auditsStore } from '@/libs/audits/+state/store';
import { auditWorkplacesStore } from '@/libs/audit-workplaces/+state/store';
import { validate as isValidUUID } from 'uuid';
import { cloneDeep } from 'lodash';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';

@Component({
    components: {
        IssueSolvingItem,
        IssueSolvingItemSmall,
        DashboardCardView,
    },
})
export default class DashboardTodayIssueSolving extends Page {
    @Prop() tenantId!: string;
    protected tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    get issueSolvingList(): PlanItemModel[] {
        let items = [...planItemsStore.useGetter(CrudGetter.DataBasic, 'DashboardTodayIssueSolving')] as PlanItemModel[];
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        const activeStations = activeModeService.getActiveStations();
        const activeWorkplaces = activeModeService.getActiveWorkplaces();
        const activeLocations = activeModeService.getActiveLocations();
        const locationScopes = locations
            .filter((x) => x.locationParents!.some((y) => activeLocations.includes(y.parentalLocationId!)))
            .map((x) => x.id!)
            .concat(activeLocations!);
        if (activeStations.length > 0) {
            items = items.filter((x) => activeStations.includes(x.stationId!));
        } else if (activeWorkplaces.length > 0) {
            items = items.filter((x) => activeWorkplaces.includes(x.workplaceId!));
        } else if (activeLocations.length > 0) {
            const workplacesToFilter = workplaces.filter((x) => locationScopes.includes(x.locationId!)).map((x) => x.id!);
            items = items.filter((x) => workplacesToFilter.includes(x.workplaceId!));
        }
        return items;
    }

    get auditEscalationGroups(): AuditEscalationGroupModel[] {
        return auditEscalationGroupsStore.useGetter(CrudGetter.Data);
    }

    get loading(): boolean {
        return auditsStore.useGetter(CrudGetter.Pending) || auditWorkplacesStore.useGetter(CrudGetter.Pending);
    }

    get audits(): AuditModel[] {
        const activeWorkplace = window.localStorage.ActiveWorkplace;
        let workplaces: string[] = [];
        try {
            if (isValidUUID(activeWorkplace)) {
                workplaces = [activeWorkplace as string];
            }
        } catch (e) {
            // ts-ignore
        }
        try {
            workplaces = JSON.parse(window.localStorage.ActiveWorkplace) as string[];
        } catch (e) {
            // ts-ignore
        }
        let items: AuditModel[] = [...auditsStore.useGetter(CrudGetter.Data)] ?? [];
        const auditWorkplaces: AuditWorkplaceModel[] = [...auditWorkplacesStore.useGetter(CrudGetter.Data)] ?? [];
        if (workplaces && workplaces.length > 0) {
            items = items.filter((x) =>
                auditWorkplaces.filter((w) => w.auditId == x.id!).some((w) => workplaces.contains(w.workplaceId!))
            );
        }
        return items;
    }

    get canMakeReport(): boolean {
        return this.hasPermissions([PermissionTypeEnum.ReportIssueSolving]);
    }

    get hasMore(): boolean {
        return (planItemsStore.useGetter(CrudGetter.DataStatsBasic, 'DashboardTodayIssueSolving')?.rowCount ?? 0) > 8;
    }

    mounted() {
        this.loadAudits();
        this.loadSolvingList();
        this.loadAuditEscalationGroups();
    }

    loadAudits() {
        const filters: CrudQueryPredicate[] = [
            {
                field: 'processType',
                op: 'eq',
                comparand: ProcessTypeEnum.IssueSolving,
            },
        ];
        if (!this.tenantUser.accessAllCategories) {
            filters.push({ field: 'category.categoryUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        const payload = createCrudQueryPayload<AuditModel>({ field: 'name', index: 1, order: 'asc' }, filters);
        auditsStore.dispatch(CrudAction.GetAll, payload);
        const payloadWorkplaces = cloneDeep(payload);
        payloadWorkplaces.query.data.orderby.forEach((e) => {
            e.field = `audit.${e.field}`;
        });
        payloadWorkplaces.query.data.predicates.forEach((e) => {
            e.field = `audit.${e.field}`;
        });
        auditWorkplacesStore.dispatch(CrudAction.GetAll, payloadWorkplaces);
    }

    loadSolvingList() {
        const groups: GroupModel[] = groupsStore.useGetter(CrudGetter.Data) ?? [];
        const myGroupIds = groups
            .filter((x) => x.groupUsers?.some((n) => n.tenantUserId == this.tenantUser.id))
            .map((x) => x.id!);
        planItemsStore.dispatch(
            CrudAction.GetAllBasic,
            createCrudQueryPayload(
                { field: 'time', index: 1, order: 'desc' },
                [
                    {
                        field: 'isDeactivated',
                        op: 'eq',
                        comparand: false,
                    },
                    { field: 'processType', op: 'eq', comparand: ProcessTypeEnum.IssueSolving },
                    { field: 'audit.auditEscalationGroups.groupId', op: 'in', comparand: myGroupIds },
                    {
                        field: 'id',
                        op: 'wrap',
                        comparand: [
                            {
                                field: 'planItemState',
                                op: 'eq',
                                comparand: 'WaitingToPerform',
                                junction: 'or',
                            },
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'isWorkplaceShutdown',
                                        op: 'eq',
                                        comparand: true,
                                    },
                                    {
                                        field: 'shutdownToDate',
                                        op: 'eq',
                                        comparand: null,
                                    },
                                ],
                                junction: 'or',
                            },
                        ],
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 8,
                },
                false,
                'DashboardTodayIssueSolving'
            )
        );
    }

    loadAuditEscalationGroups() {
        auditEscalationGroupsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<AuditEscalationGroupModel>(undefined, [
                { field: 'escalationGroupType', op: 'eq', comparand: EscalationGroupTypeEnum.IssueEscalation },
            ])
        );
    }
}
